
import React, { useState } from 'react';
import SEOHead from '@/components/SEOHead';
import { useAuth } from "@/hooks/useAuth";
import { useInternalAuth } from "@/hooks/useInternalAuth";
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Tag, Clock, Star } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const Deals = () => {
  const { user } = useAuth();
  const { user: internalUser } = useInternalAuth();
  const isMobile = useIsMobile();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ['deals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const categories = ['all', ...new Set(deals.map(deal => deal.category))];
  const filteredDeals = selectedCategory === 'all' 
    ? deals 
    : deals.filter(deal => deal.category === selectedCategory);

  const handleClaimDeal = async (dealId: string) => {
    if (!user && !internalUser) return;
    
    try {
      const { error } = await supabase
        .from('deal_claims')
        .insert([{
          deal_id: dealId,
          user_id: user?.id || internalUser?.id
        }]);
      
      if (error) throw error;
    } catch (error) {
      console.error('Error claiming deal:', error);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-black w-full">
      <SEOHead title="Deals" description="Exclusive deals and discounts for athletes, coaches, and sports enthusiasts." path="/deals" />
      {!isMobile && <Header />}
      {isMobile && <MobileHeader />}
      
      <main className={`flex-grow w-full ${
        isMobile 
          ? 'pt-16 mb-16 py-6 px-4' 
          : 'mt-24 mb-6 py-6 md:py-12 px-4 md:px-6'
      }`}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Exclusive Deals</h1>
            <p className="text-gray-400">Discover amazing deals and discounts for sports enthusiasts</p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category 
                  ? "bg-sports-green text-black hover:bg-green-600" 
                  : "border-gray-600 text-gray-300 hover:bg-gray-800"
                }
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </Button>
            ))}
          </div>

          {/* Deals Grid */}
          {isLoading ? (
            <div className="text-center text-gray-400">Loading deals...</div>
          ) : filteredDeals.length === 0 ? (
            <div className="text-center text-gray-400">No deals available at the moment.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredDeals.map((deal) => (
                <Card key={deal.id} className="bg-gray-900 border-gray-700 hover:bg-gray-800 transition-colors">
                  <CardHeader>
                    <div className="flex justify-between items-start mb-2">
                      <Badge variant="secondary" className="bg-sports-green text-black">
                        <Tag className="h-3 w-3 mr-1" />
                        {deal.category}
                      </Badge>
                      {deal.rating && (
                        <div className="flex items-center text-yellow-400">
                          <Star className="h-4 w-4 mr-1 fill-current" />
                          <span className="text-sm">{deal.rating}</span>
                        </div>
                      )}
                    </div>
                    <CardTitle className="text-white text-lg">{deal.title}</CardTitle>
                    <p className="text-sports-green font-semibold">{deal.company}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="text-2xl font-bold text-sports-green">
                        {deal.discount}
                      </div>
                      
                      {deal.description && (
                        <p className="text-gray-300 text-sm">{deal.description}</p>
                      )}
                      
                      <div className="flex items-center text-gray-400 text-sm">
                        <Clock className="h-4 w-4 mr-2" />
                        Expires: {new Date(deal.expires_at).toLocaleDateString()}
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => handleClaimDeal(deal.id)}
                          className="w-full bg-sports-green text-black hover:bg-green-600"
                          disabled={!user && !internalUser}
                        >
                          {user || internalUser ? 'Claim Deal' : 'Login to Claim'}
                        </Button>
                        
                        <Button
                          variant="outline"
                          onClick={() => window.open(deal.link, '_blank')}
                          className="w-full border-gray-600 text-gray-300 hover:bg-gray-700"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Visit Store
                        </Button>
                      </div>
                      
                      {deal.code && (
                        <div className="bg-gray-800 p-3 rounded border border-gray-600">
                          <p className="text-xs text-gray-400 mb-1">Promo Code:</p>
                          <p className="font-mono text-sports-green font-semibold">{deal.code}</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>
      
      {!isMobile && <Footer />}
      {isMobile && <MobileBottomNav />}
    </div>
  );
};

export default Deals;
